/**
 * Shared node materializer — turns a RecipeStep into a GraphNode + its
 * incoming GraphEdges, with all schema knowledge applied (parameter defaults,
 * discriminated unions, OHLCV field sugar, multi-input handles).
 *
 * Extracted from recipe-builder so buildGraphFromRecipe and graph-ops
 * materialize nodes identically. Pure: never mutates the input step.
 */
import type { UniversalNodeSchema } from '@/features/backtest/schemas/universal-node-schema'
import type { GraphNode, GraphEdge } from '@/features/backtest/schemas/backtest-types'
import type { RecipeStep } from '@/features/backtest/utils/recipe-types'
import type { EngineError, MaterializedInput } from './types'

const OHLCV_FIELDS = new Set(['open', 'high', 'low', 'close', 'volume'])

export interface MaterializeContext {
  /** stepId → schema, for every step in the recipe/draft (sources included). */
  schemaById: Map<string, UniversalNodeSchema>
  position: { x: number; y: number }
  /** Set for the first priceData node in a build. */
  isPrimary?: boolean
}

export interface MaterializeOutput {
  node: GraphNode
  edges: GraphEdge[]
  errors: EngineError[]
}

export function materializeNode(step: RecipeStep, ctx: MaterializeContext): MaterializeOutput {
  const errors: EngineError[] = []
  const schema = ctx.schemaById.get(step.id)
  if (!schema) {
    return {
      node: { id: step.id, type: step.type, position: ctx.position, data: {} },
      edges: [],
      errors: [
        {
          code: 'UNKNOWN_TYPE',
          stepId: step.id,
          message: `Unknown node type "${step.type}"`,
        },
      ],
    }
  }

  // Effective params = step.params + derived discriminated-union modes.
  // Derived into a copy — the legacy builder mutated step.params, which
  // corrupted frozen/shared recipes.
  const effectiveParams: Record<string, unknown> = { ...step.params }

  const { inputs, edges } = buildInputsAndEdges(step, schema, ctx, effectiveParams, errors)

  validateParamNames(step, schema, errors)

  const parameters = schema.parameters.map((p) => ({
    name: p.name,
    value: effectiveParams[p.name] ?? p.default,
  }))
  // A preset type (`timeSlice:premarket-high`, which EdgeScript's session.premarketHigh
  // lowers to) hides the settings it fixes; the node carries them, as one placed from the
  // palette does (use-node-operations), so serialization hands the engine a configured
  // base node rather than one at the base type's defaults.
  if (schema.presetParameters)
    for (const [name, value] of Object.entries(schema.presetParameters))
      if (!parameters.some((p) => p.name === name)) parameters.push({ name, value })

  const outputs = schema.outputs.map((o) => ({
    id: o.id,
    name: o.id,
    type: o.type,
    displayName: o.displayName,
  }))

  const node: GraphNode = {
    id: step.id,
    type: step.type,
    position: ctx.position,
    data: {
      id: step.id,
      label: buildLabel(step, schema),
      type: step.type,
      category: schema.category,
      parameters,
      inputs,
      outputs,
      ...(step.type === 'priceData' && { isPrimary: ctx.isPrimary === true }),
      ...(schema.presetId && { presetId: schema.presetId }),
    },
  }

  return { node, edges, errors }
}

// ── Param name validation ───────────────────────────────────

/** `${inputId}_value` is the legacy static-value convention — always allowed. */
function isConventionParam(name: string, schema: UniversalNodeSchema): boolean {
  return schema.inputs.some((i) => name === `${i.id}_value`)
}

function validateParamNames(
  step: RecipeStep,
  schema: UniversalNodeSchema,
  errors: EngineError[],
): void {
  if (!step.params) return
  const known = new Set(schema.parameters.map((p) => p.name))
  for (const name of Object.keys(step.params)) {
    if (known.has(name) || isConventionParam(name, schema)) continue
    errors.push({
      code: 'PARAM_UNKNOWN',
      stepId: step.id,
      message: `"${step.type}" has no parameter "${name}"`,
      suggestion: `Known parameters: ${[...known].join(', ') || '(none)'}`,
    })
  }
}

// ── Inputs + edges ──────────────────────────────────────────

function buildInputsAndEdges(
  step: RecipeStep,
  schema: UniversalNodeSchema,
  ctx: MaterializeContext,
  effectiveParams: Record<string, unknown>,
  errors: EngineError[],
): { inputs: MaterializedInput[]; edges: GraphEdge[] } {
  const inputs: MaterializedInput[] = []
  const edges: GraphEdge[] = []

  const addEdgeForRef = (targetHandle: string, ref: string) => {
    const resolved = resolveRef(ref, ctx.schemaById, step.id, targetHandle, errors)
    if (!resolved) return null
    edges.push({
      id: `e-${resolved.sourceId}-${resolved.outputHandle}-${step.id}-${targetHandle}`,
      source: resolved.sourceId,
      sourceHandle: resolved.outputHandle,
      target: step.id,
      targetHandle,
    })
    return resolved
  }

  for (const inputDef of schema.inputs) {
    const raw = step.inputs?.[inputDef.id]

    if (typeof raw === 'string') {
      // Connected to another node
      const resolved = addEdgeForRef(inputDef.id, raw)
      inputs.push({
        name: inputDef.id,
        inputType: 'node',
        source: resolved?.sourceId ?? raw.split('.')[0],
        field: resolved?.field ?? inputDef.fieldSelection?.defaultField,
        value: null,
      })
      if (inputDef.discriminatedUnion) {
        const paramName = inputDef.discriminatedUnion.parameterName
        if (effectiveParams[paramName] === undefined) effectiveParams[paramName] = 'node'
      }
    } else if (raw !== undefined) {
      // Literal → static-mode input (requires discriminated-union support)
      if (!inputDef.discriminatedUnion) {
        errors.push({
          code: 'INVALID_REF',
          stepId: step.id,
          handle: inputDef.id,
          message: `Input "${inputDef.id}" does not accept a literal value`,
          suggestion: 'Connect a node output ref like "stepId.outputHandle" instead',
        })
        inputs.push({ name: inputDef.id, inputType: 'node', source: null, value: null })
      } else {
        inputs.push({ name: inputDef.id, inputType: 'static', source: null, value: raw })
        effectiveParams[inputDef.discriminatedUnion.parameterName] = 'static'
      }
    } else if (inputDef.discriminatedUnion) {
      // No input given — static mode via legacy param convention, or unconnected
      const paramValue = step.params?.[inputDef.discriminatedUnion.parameterName]
      if (paramValue === 'static') {
        const staticValue =
          step.params?.[`${inputDef.id}_value`] ?? inputDef.discriminatedUnion.defaultStaticValue
        inputs.push({ name: inputDef.id, inputType: 'static', source: null, value: staticValue })
      } else {
        inputs.push({ name: inputDef.id, inputType: 'node', source: null, value: null })
      }
    } else {
      // Unconnected input
      inputs.push({ name: inputDef.id, inputType: 'node', source: null, value: null })
    }
  }

  // Multi-input nodes (e.g. merge) — dynamic handles beyond the schema
  if (schema.inputHandler?.type === 'multi' && step.inputs) {
    const schemaInputIds = new Set(schema.inputs.map((i) => i.id))
    for (const [handle, raw] of Object.entries(step.inputs)) {
      if (schemaInputIds.has(handle)) continue
      if (typeof raw !== 'string') {
        errors.push({
          code: 'INVALID_REF',
          stepId: step.id,
          handle,
          message: `Multi-input "${handle}" requires a node ref, got a literal`,
        })
        continue
      }
      const resolved = addEdgeForRef(handle, raw)
      inputs.push({
        name: handle,
        inputType: 'node',
        source: resolved?.sourceId ?? raw.split('.')[0],
        field: resolved?.field,
        value: null,
      })
    }
  }

  return { inputs, edges }
}

// ── Reference resolution ────────────────────────────────────

export interface ResolvedRef {
  sourceId: string
  outputHandle: string
  field?: string
}

export function resolveRef(
  ref: string,
  schemaById: Map<string, UniversalNodeSchema>,
  stepId: string,
  handle: string,
  errors: EngineError[],
): ResolvedRef | null {
  const dotIdx = ref.indexOf('.')
  if (dotIdx === -1) {
    errors.push({
      code: 'INVALID_REF',
      stepId,
      handle,
      message: `Invalid ref "${ref}"`,
      suggestion: 'Use "sourceId.outputHandle" (or "sourceId.close" for OHLCV fields)',
    })
    return null
  }

  const sourceId = ref.slice(0, dotIdx)
  const handlePart = ref.slice(dotIdx + 1)
  const sourceSchema = schemaById.get(sourceId)

  if (!sourceSchema) {
    errors.push({
      code: 'UNKNOWN_STEP',
      stepId,
      handle,
      message: `Ref "${ref}" points at unknown step "${sourceId}"`,
    })
    return null
  }

  // OHLCV field sugar (e.g. "price.close")
  const ohlcvOutput = sourceSchema.outputs.find((o) => o.type === 'ohlcv')
  if (ohlcvOutput && OHLCV_FIELDS.has(handlePart)) {
    return { sourceId, outputHandle: ohlcvOutput.id, field: handlePart }
  }

  const matchedOutput = sourceSchema.outputs.find((o) => o.id === handlePart)
  if (!matchedOutput) {
    errors.push({
      code: 'UNKNOWN_HANDLE',
      stepId,
      handle,
      message: `"${sourceId}" has no output "${handlePart}"`,
      suggestion: `Available: ${sourceSchema.outputs.map((o) => o.id).join(', ')}`,
    })
    return null
  }

  return {
    sourceId,
    outputHandle: handlePart,
    ...(matchedOutput.type === 'ohlcv' ? { field: 'close' } : {}),
  }
}

// ── Label ───────────────────────────────────────────────────

export function buildLabel(step: RecipeStep, schema: UniversalNodeSchema): string {
  const base = schema.displayName
  const period = step.params?.period
  if (period != null) return `${base} (${period})`
  return base
}
